import React, { useCallback, useEffect } from 'react';
import { Box, Button, ContentFrame, LayoutContent, LayoutContentHeader, LayoutControls, useTheme, } from '@pinwheel/origami';
import { useSelector } from 'react-redux';
import { useOpts } from '../../context';
import { useAppDispatch } from '../../state';
import { generateManualInstructionsAttemptId } from '../../state/slices/slice-segment';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
import { selectBankInfosForManualInstructions, selectManualInstructions, } from '../../state/slices/slice-link-api';
import { useReturnButtonText } from '../../hooks/useReturnButtonText';
import { selectShouldShowDDFormsFallback } from '../../state/slices/slice-dd-form';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import { selectFeatureFlags } from '../../state/slices/slice-link-params';
import AccountAndRoutingNumberDisplay from './AccountAndRoutingNumberDisplay';
import { useDDFormContext } from '../../hooks/useDDFormContext';
import useOrgName from '../../hooks/useOrgName';
var HYPERLINK_PREFIX = /^https?:\/\//;
var BANK_INFO_MARKER = '<BANK_INFO>';
var ORG_NAME_MARKER = '<ORG_NAME>';
/**
 * Returns a <span> element of the inputted `text` with all URLs replaced with
 * an actual hyperlink element. Also replaces each "/" with a
 * "/ + ZERO WIDTH SPACE (U+200B)" so links will only line break after slashes.
 *
 * @param text - string - the text to parse
 * @return ReactElement
 */
var getParsedHyperlinkListItem = function (text) {
    // Slightly overengineered, but the purpose of the logic below is to ensure
    // that the text in the resulting <span> is continuous by joining together all
    // consecutive text parts. Had we not done this and simply rendered the array
    // of split text parts, the resulting HTML would look something like this,
    // which would have minor hindrances to text selection and screen readers:
    // <span>"This " "is " "a " "sentence"</span>
    var elementsToRender = [];
    text.split(' ').forEach(function (chunk, i) {
        var lastElement = elementsToRender.length
            ? elementsToRender[elementsToRender.length - 1]
            : null;
        if (HYPERLINK_PREFIX.test(chunk)) {
            if (lastElement) {
                if (typeof lastElement === 'string') {
                    elementsToRender[elementsToRender.length - 1] += ' ';
                }
                else {
                    elementsToRender.push(' ');
                }
            }
            elementsToRender.push(React.createElement("a", { key: i, href: chunk, target: "_blank", rel: "noopener noreferrer" }, chunk.replace(HYPERLINK_PREFIX, '').replace(/\//g, '/\u200B')));
        }
        else if (lastElement) {
            if (typeof lastElement === 'string') {
                elementsToRender[elementsToRender.length - 1] += " ".concat(chunk);
            }
            else {
                elementsToRender.push(" ".concat(chunk));
            }
        }
        else {
            elementsToRender.push(chunk);
        }
    });
    return React.createElement("span", null, elementsToRender);
};
var replaceOrgName = function (text, orgName) {
    if (text.includes(ORG_NAME_MARKER)) {
        var regex = new RegExp(ORG_NAME_MARKER, 'g');
        return text.replace(regex, orgName);
    }
    return text;
};
var ManualInstructions = function () {
    var resolveColor = useTheme().resolveColor;
    var getLocalizedString = useGetLocalizedString();
    var exit = useOpts().exit;
    var dispatch = useAppDispatch();
    var returnButtonText = useReturnButtonText();
    var instructions = useSelector(selectManualInstructions);
    var bankInfos = useSelector(selectBankInfosForManualInstructions);
    var shouldShowDDForms = useSelector(selectShouldShowDDFormsFallback);
    var navigate = useLinkNavigate();
    var featureFlags = useSelector(selectFeatureFlags);
    var setDDFormEntryPoint = useDDFormContext().setDDFormEntryPoint;
    var orgName = useOrgName();
    useEffect(function () {
        dispatch(generateManualInstructionsAttemptId());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var getAccountAndRoutingNumberListItem = useCallback(function (liNumber, color) {
        if (bankInfos &&
            bankInfos.length > 0 &&
            featureFlags.manualInstructionsShowARNumbers) {
            return (React.createElement("li", { "data-testid": "manual-instruction-".concat(liNumber), key: liNumber, style: {
                    color: color,
                } },
                React.createElement("span", null, getLocalizedString('USE_ACCOUNT_DETAILS')),
                React.createElement(AccountAndRoutingNumberDisplay, null)));
        }
        return (React.createElement("li", { "data-testid": "manual-instruction-".concat(liNumber), key: liNumber, style: {
                color: color,
            } },
            React.createElement("span", null, getLocalizedString('ROUTING_AND_ACCOUNT', [orgName]))));
    }, [
        bankInfos,
        getLocalizedString,
        featureFlags.manualInstructionsShowARNumbers,
        orgName,
    ]);
    return (React.createElement(ContentFrame, { "data-testid": "TemplateForm" },
        React.createElement(LayoutContent, { "data-testid": "ManualInstructions" },
            React.createElement(LayoutContentHeader, { title: getLocalizedString('UPDATE_DD'), subtitle: getLocalizedString('FOLLOW_INSTRUCTIONS') }),
            React.createElement(Box, { as: "ol", sx: {
                    listStyleType: 'none',
                    marginLeft: 0,
                    paddingInlineStart: 0,
                    '& a': {
                        color: {
                            type: 'theme',
                            colorLegacy: 'Primary/50',
                            colorNext: 'brand/default',
                        },
                        fontWeight: '500',
                    },
                    '& li': {
                        '& span': {
                            display: 'table-cell',
                        },
                    },
                    '& > li': {
                        counterIncrement: 'customlistcounter',
                        marginBottom: '3',
                    },
                    '& > li:last-child': {
                        marginBottom: '0',
                    },
                    '& > li::before': {
                        content: "counter(customlistcounter) ' '",
                        fontWeight: 'bold',
                        float: 'left',
                        width: '24px',
                        height: '24px',
                        backgroundColor: {
                            type: 'theme',
                            colorLegacy: 'Primary/70',
                            colorNext: 'brand/accent',
                        },
                        color: {
                            type: 'theme',
                            colorLegacy: 'Neutral/0',
                            colorNext: 'background/default',
                        },
                        textAlign: 'center',
                        marginRight: '1',
                        borderRadius: '50%',
                        lineHeight: '24px',
                        top: '-2px',
                    },
                } }, instructions.map(function (text, i) {
                if (text === BANK_INFO_MARKER) {
                    return getAccountAndRoutingNumberListItem(i, resolveColor({
                        type: 'theme',
                        colorLegacy: 'Neutral/80',
                        colorNext: 'neutral/default',
                    }));
                }
                // Need to wrap li's text with a span so multiline text strings
                // have margins that align with the first row of text.
                return (React.createElement("li", { "data-testid": "manual-instruction-".concat(i), key: i, style: {
                        color: resolveColor({
                            type: 'theme',
                            colorLegacy: 'Neutral/80',
                            colorNext: 'neutral/default',
                        }),
                    } }, getParsedHyperlinkListItem(replaceOrgName(text, orgName))));
            }))),
        React.createElement(LayoutControls, null,
            shouldShowDDForms && (React.createElement(Button, { variant: "ghost", "data-testid": "manualInstructionsDisplay-useDDForm", onClick: function () {
                    setDDFormEntryPoint('manual_instructions_display');
                    navigate('/dd-form/primer');
                } }, getLocalizedString('USE_A_FORM_INSTEAD'))),
            React.createElement(Button, { dataTestId: "doneButton", onClick: function () { return exit(); }, style: { marginBottom: '24px' } }, returnButtonText))));
};
export default ManualInstructions;
